/**
 * Описание тайтла — столько строк, сколько помещается рядом с постером.
 *
 * Правило страницы (владелец): ряд кнопок стоит низом по нижнему краю постера.
 * Колонка текста для этого не должна быть выше постера, а её высоту задаёт
 * описание — единственный блок, который можно укоротить. Сколько строк ему
 * отведено самое большее, задают стили (`--overview-lines`), а сколько
 * помещается на деле, зависит от высоты постера, длины названия и слогана —
 * без подгонки кнопки уезжали под линию постера на 10–250 px.
 *
 * Здесь число строк подгоняется под настоящее место: от верха описания до
 * линии, на которой блок кнопок встаёт низом по низу постера. Число уходит в
 * `--overview-fit`; стили берут его вместо `--overview-lines`. Посчитать это в
 * самих стилях нельзя: обрезка по строкам принимает только целое число.
 *
 * Постер на сайте небольшой (200–300 px шириной), и у названия в две строки
 * со слоганом даже двум строкам описания места при обычных шагах нет. Тогда
 * колонка помечается `data-tight` — стили собирают шаги над слоганом и
 * описанием плотнее, и кнопки остаются на линии низа постера.
 */

/** Меньше двух строк описание не бывает: одна строка — обрывок, а не текст. */
const MIN_LINES = 2;

/**
 * Сколько строк показать: сколько влезает в `free` точек, но не больше `limit`
 * и не меньше двух (тогда колонка растянется, и кнопки уйдут ниже постера —
 * это запасной ход, а не поломка).
 */
export function overviewLinesThatFit(free: number, lineHeight: number, limit: number): number {
  if (!(lineHeight > 0) || !Number.isFinite(free)) return limit;
  // Полточки — на дробные размеры: строка, которой не хватило 0,3 px, помещается.
  const fit = Math.floor((free + 0.5) / lineHeight);
  return Math.max(MIN_LINES, Math.min(limit, fit));
}

/** Тесно: при обычных шагах колонки не помещаются даже две строки описания. */
export function overviewIsTight(free: number, lineHeight: number): boolean {
  if (!(lineHeight > 0) || !Number.isFinite(free)) return false;
  return Math.floor((free + 0.5) / lineHeight) < MIN_LINES;
}

/**
 * Следит за местом под описание и держит `--overview-fit` в актуальном виде.
 * Возвращает функцию отключения.
 */
export function installOverviewFit(overview: HTMLElement): () => void {
  const body = overview.parentElement;
  const layout = overview.closest<HTMLElement>('.title-layout');
  const poster = layout?.querySelector<HTMLElement>('.title-poster') ?? null;
  const actions = body?.querySelector<HTMLElement>('.title-cta') ?? null;
  if (body === null || layout === null || poster === null || actions === null) return () => undefined;

  const fit = (): void => {
    const style = getComputedStyle(overview);
    const limit = Number.parseInt(style.getPropertyValue('--overview-lines'), 10);
    const lineHeight = Number.parseFloat(style.lineHeight);
    // Место меряется при ОБЫЧНЫХ шагах: иначе пометка «тесно» сама освобождала
    // бы место, снималась и ставилась снова.
    body.removeAttribute('data-tight');
    const posterBox = poster.getBoundingClientRect();
    const box = overview.getBoundingClientRect();
    // Телефон: описание идёт под постером во всю ширину и целиком — подгонять нечего.
    if (!Number.isFinite(limit) || box.left < posterBox.right) {
      overview.style.removeProperty('--overview-fit');
      return;
    }
    const room = (): number => posterBox.bottom - actions.getBoundingClientRect().height - overview.getBoundingClientRect().top;
    let free = room();
    if (overviewIsTight(free, lineHeight)) {
      body.setAttribute('data-tight', '');
      free = room();
    }
    const lines = String(overviewLinesThatFit(free, lineHeight, limit));
    if (overview.style.getPropertyValue('--overview-fit') !== lines) overview.style.setProperty('--overview-fit', lines);
  };

  fit();
  // Место меняют: размер окна (постер, отступы), перенос названия и строки с
  // годом (шрифт догрузился), состав кнопок. Само описание не наблюдаем — его
  // высоту меняем мы.
  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(fit) : null;
  observer?.observe(poster);
  observer?.observe(actions);
  const head = body.querySelector('.title-head');
  if (head !== null) observer?.observe(head);
  window.addEventListener('resize', fit);
  return () => {
    observer?.disconnect();
    window.removeEventListener('resize', fit);
  };
}
